import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw, Wrench, X } from 'lucide-react';
import { onMaintenance, publicAPI } from '../../api/client';
import './Maintenance.css';

const POLL_MS = 60000;
const DISMISS_KEY = 'tcx-maintenance-banner';
const when = (iso) => (iso ? new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`)
  .toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null);

/**
 * Around a company's pages: while TeleCalX is in maintenance (checked every minute, and whenever the API answers 503
 * maintenance) customers see the maintenance page instead; in the days before a scheduled window, a banner. When
 * maintenance ends, the page reloads to start fresh. Pages under openPaths stay usable (sign-in, for the team).
 */
export default function MaintenanceGate({ children, openPaths = [] }) {
  const { pathname } = useLocation();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(() => { try { return sessionStorage.getItem(DISMISS_KEY); } catch { return null; } });
  const check = useCallback(() => publicAPI.maintenance().then((r) => { setState(r.data); return r.data; }).catch(() => null), []);

  useEffect(() => {
    check();
    const timer = setInterval(check, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    const stop = onMaintenance((m) => setState(m));
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); stop(); };
  }, [check]);

  const [wasActive, setWasActive] = useState(false);
  useEffect(() => {
    if (state?.active) setWasActive(true);
    else if (state && wasActive) window.location.reload();
  }, [state, wasActive]);

  if (state?.active && !openPaths.some((p) => pathname.startsWith(p))) {
    const back = when(state.ends_at);
    return (
      <div className="tcx-maint">
        <div className="tcx-maint-card" role="status">
          <span className="tcx-maint-icon"><Wrench size={30} /></span>
          <h1>{state.title}</h1>
          <p>{state.message}</p>
          {back && <small>Expected back by {back}</small>}
          <button type="button" onClick={check}><RefreshCw size={14} /> Try again</button>
        </div>
      </div>
    );
  }

  const upcoming = state?.upcoming && dismissed !== state.upcoming.starts_at ? state.upcoming : null;
  const dismiss = () => {
    setDismissed(upcoming.starts_at);
    try { sessionStorage.setItem(DISMISS_KEY, upcoming.starts_at); } catch { /* private mode */ }
  };
  return (
    <>
      {upcoming && (
        <div className="tcx-maint-banner" role="status">
          <Wrench size={15} />
          <span><b>Scheduled maintenance:</b> {upcoming.title}, {when(upcoming.starts_at)} – {when(upcoming.ends_at)}. TeleCalX will be unavailable during this time.</span>
          <button type="button" aria-label="Dismiss" onClick={dismiss}><X size={15} /></button>
        </div>
      )}
      {children}
    </>
  );
}
